import { computed, onMounted, ref, type ComputedRef } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw, type RouteLocationRaw } from 'vue-router';

// Module-level hydration gate. False during SSR/pre-render and the first
// client render (so pre-rendered HTML and the hydrated DOM both show the
// original copy), true after mount. Shared across every consumer so all
// views flip together once hydrated.
const hydrated = ref(false);

interface CopyVariant {
  isCopyV2: ComputedRef<boolean>;
  copyLink: (to: string | RouteLocationRaw) => string | RouteLocationRaw;
  originalLink: () => RouteLocationRaw;
}

// Hidden comparison variant selected with ?v=2. Never persisted: removing
// the query restores the original copy. No router guard keeps it alive.
export function useCopyVariant(): CopyVariant {
  const route = useRoute();
  const router = useRouter();

  onMounted(() => {
    hydrated.value = true;
  });

  const isCopyV2 = computed<boolean>(() => hydrated.value && route.query.v === '2');

  // Internal navigation helper: preserves ?v=2 while the variant is active
  // (keeping the target's own query and hash via router.resolve), returns
  // the target untouched otherwise.
  function copyLink(to: string | RouteLocationRaw): string | RouteLocationRaw {
    if (!isCopyV2.value) return to;
    const resolved = router.resolve(to);
    const target: { path: string; query: LocationQueryRaw; hash?: string } = {
      path: resolved.path,
      query: { ...resolved.query, v: '2' },
    };
    if (resolved.hash) target.hash = resolved.hash;
    return target;
  }

  // V2-only escape hatch: drops ONLY the v param, preserving any other
  // query (e.g. utm) and the hash. No sticky state involved.
  function originalLink(): RouteLocationRaw {
    const query: LocationQueryRaw = { ...route.query };
    delete query.v;
    const target: { path: string; query: LocationQueryRaw; hash?: string } = {
      path: route.path,
      query,
    };
    if (route.hash) target.hash = route.hash;
    return target;
  }

  return { isCopyV2, copyLink, originalLink };
}
